import { Box, Button } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { FormikSelect, FormikSelectAsyncPaginate } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import { routePaths } from 'global/routePaths';
import { fetchEvvDataProviders } from 'hooks/companySettings/useFetchEvvDataProviders';
import { fetchEvvProviders } from 'hooks/companySettings/useFetchEvvProviders';
import { useFetchEvvSettings } from 'hooks/companySettings/useFetchEvvSettings';
import { useFetchTimezones } from 'hooks/companySettings/useFetchTimezone';
import { useSaveEvvSettingSetting, ISaveEvvSettingSetting } from 'hooks/companySettings/useSaveEvvSettings';
import { useCompanyInfo } from 'store/useUserStore';
import toaster from 'utils/toaster';

import { initialEvvSettingData, evvSettingValidation } from './schema';
import { evvSyncEventOptions } from '../shared/options';

const EvvSettings: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);
  const companyInfo = useCompanyInfo();

  const [formData, setFormData] = useState<any>(null);

  const { data, status, refetch } = useFetchEvvSettings();
  const { data: timezoneOptions, status: timezoneStatus } = useFetchTimezones();

  const { status: saveNotificationStatus, mutateAsync } = useSaveEvvSettingSetting();

  useEffect(
    () => {
      if (status === 'success') {
        const pFormData: any = {
          evv_sync_event: data?.payload?.evv_sync_event
            ? evvSyncEventOptions.find((item: any) => item.value === data?.payload?.evv_sync_event)
            : null,
          evv_provider: data?.payload?.evv_provider ?? null,
          evv_data_provider: data?.payload?.evv_data_provider ?? null,
          timezone: data?.payload?.timezone ?? null,
        };
        setFormData(pFormData);
      }
    },
    // eslint-disable-next-line
    [status, timezoneStatus]
  );

  const handleFormSubmit = async (values: typeof initialEvvSettingData) => {
    const postData: ISaveEvvSettingSetting = {
      evv_sync_event: values.evv_sync_event ? values.evv_sync_event?.value : null,
      evv_provider: values.evv_provider ? values.evv_provider?.value : null,
      evv_data_provider: values.evv_data_provider ? values.evv_data_provider?.value : null,
      timezone: values.timezone ? values.timezone?.value : null,
    };

    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  const evvLoadOptions = async (search: any, apiCallback: () => Promise<any>) => {
    try {
      const response = await apiCallback();

      const filteredOptions = (response?.payload || []).filter((option: any) =>
        option.value.toLowerCase().includes(search.toLowerCase())
      );

      return {
        options: filteredOptions,
        hasMore: false,
      };
    } catch (err: any) {
      toaster.error({ description: err?.message });
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  if (status === 'loading' || !formData) return <LogoLoader />;

  if (!companyInfo.evvSetting?.has_evv) return <Navigate to={routePaths.companySettings.general} replace={true} />;

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton> {t('companySettings:evvSettings')}</Title>
      </Box>
      <Box mt="26px">
        <Formik
          initialValues={formData}
          validationSchema={evvSettingValidation}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate>
              <Box w="390px">
                <FormikSelect
                  name="evv_sync_event"
                  label={t('companySettings:syncEvvVisit')}
                  options={evvSyncEventOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('companySettings:selectEvvVisit'),
                    value:
                      typeof values.evv_sync_event === 'object'
                        ? values.evv_sync_event
                        : (evvSyncEventOptions || []).find((option: any) => option.value === values.evv_sync_event) ||
                          '',
                  }}
                />
                <FormikSelectAsyncPaginate
                  name="evv_provider"
                  label={t('companySettings:evvProvider')}
                  loadOptions={(search: string) => evvLoadOptions(search, fetchEvvProviders)}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('companySettings:selectEvvProvider'),
                  }}
                />
                <FormikSelectAsyncPaginate
                  name="evv_data_provider"
                  label={t('companySettings:evvDataProvider')}
                  loadOptions={(search: string) => evvLoadOptions(search, fetchEvvDataProviders)}
                  selectProps={{
                    placeholder: t('companySettings:selectEvvDataProvider'),
                  }}
                />
                <FormikSelect
                  name="timezone"
                  label={t('companySettings:evvTimezone')}
                  options={timezoneOptions}
                  selectProps={{
                    defaultOptions: [],
                    placeholder: t('companySettings:selectEvvTimezone'),
                    isLoading: timezoneStatus === 'loading',
                    value:
                      typeof values.timezone === 'object'
                        ? values.timezone
                        : (timezoneOptions || []).find((option: any) => option.value === values.timezone) || '',
                  }}
                />
              </Box>

              <Button
                mt="15"
                type="submit"
                colorScheme="secondary"
                isLoading={saveNotificationStatus === 'loading'}
                minWidth="100px"
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
export default EvvSettings;
